import { useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button, Icon } from '../../ui';
import {
  CLIENT_NOTE_WORD_MAX,
  docToMarkdown,
  markdownToHtml,
  noteWordCount,
} from '../../../lib/clientNoteMarkdown';

/**
 * ClientNoteEditor — TipTap over the paste-ready client note.
 *
 * The note is stored and copied as markdown; this edits it as rich text so the
 * shape on screen is the shape Trello will show. The schema is deliberately cut
 * down to what Trello renders: no underline (it has none), no tables, no
 * images, no HTML. What cannot be typed cannot end up in the comment.
 */

type ToolProps = {
  label: string;
  title: string;
  active?: boolean;
  onClick: () => void;
  mono?: boolean;
};

function Tool({ label, title, active = false, onClick, mono = false }: ToolProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="ck-note-tool pressable"
      data-active={active ? 'true' : undefined}
      style={{ fontFamily: mono ? 'var(--font-mono)' : 'var(--font-ui)' }}
    >
      {label}
    </button>
  );
}

export default function ClientNoteEditor({
  value,
  onSave,
  onCancel,
  wordMax = CLIENT_NOTE_WORD_MAX,
  saveLabel,
}: {
  value: string;
  onSave: (markdown: string) => Promise<void>;
  onCancel: () => void;
  /** Word budget shown in the toolbar; null hides it (Page Build sections have none). */
  wordMax?: number | null;
  saveLabel?: string;
}) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [words, setWords] = useState(() => noteWordCount(value));

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        underline: false,
        link: { openOnClick: false, autolink: true },
      }),
    ],
    content: markdownToHtml(value),
    onUpdate: ({ editor: ed }) => setWords(noteWordCount(docToMarkdown(ed.getJSON()))),
  });

  if (!editor) return null;

  const openLink = () => {
    setLinkUrl(editor.getAttributes('link').href ?? '');
    setLinkOpen(true);
  };

  const applyLink = () => {
    const href = linkUrl.trim();
    const chain = editor.chain().focus().extendMarkRange('link');
    if (href) chain.setLink({ href }).run();
    else chain.unsetLink().run();
    setLinkOpen(false);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const markdown = docToMarkdown(editor.getJSON());
      if (!markdown.trim()) throw new Error('This cannot be empty.');
      await onSave(markdown);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the note.');
      setSaving(false);
    }
  };

  const over = wordMax != null && words > wordMax;

  return (
    <div className="ck-note-editor">
      <div className="ck-note-toolbar">
        <Tool
          label="H"
          title="Heading"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        />
        <Tool label="B" title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
        <Tool label="I" title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <Tool label="S" title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />
        <span className="ck-note-tool-sep" />
        <Tool label="•" title="Bulleted list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <Tool label="1." title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <Tool label="❝" title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
        <Tool label="`" title="Code" mono active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} />
        <span className="ck-note-tool-sep" />
        <Tool label="↗" title="Link" active={editor.isActive('link')} onClick={openLink} />

        <span style={{ flex: 1 }} />
        <span
          className="t-eyebrow"
          title={wordMax != null ? `The CLI budget for a client note is ${wordMax} words` : undefined}
          style={{ color: over ? 'var(--danger-400)' : 'var(--text-faint)' }}
        >
          {wordMax != null ? `${words}/${wordMax} words` : `${words} words`}
        </span>
      </div>

      {linkOpen ? (
        <div className="ck-note-linkbar">
          <Icon name="link" size={14} />
          <input
            className="ck-textarea"
            autoFocus
            value={linkUrl}
            placeholder="https://…  (empty removes the link)"
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
              if (e.key === 'Escape') setLinkOpen(false);
            }}
            style={{ flex: 1, height: 'var(--control-sm)', padding: '0 10px' }}
          />
          <Button size="sm" variant="secondary" onClick={applyLink}>
            Apply
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setLinkOpen(false)}>
            Cancel
          </Button>
        </div>
      ) : null}

      <EditorContent editor={editor} />

      <div className="ck-note-footer">
        {error ? (
          <p style={{ margin: '0 0 8px', fontSize: 'var(--text-sm)', color: 'var(--danger-400)' }}>{error}</p>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Button size="sm" variant="primary" icon="check" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (saveLabel ?? 'Save note')}
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          {over ? (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
              Over the {wordMax}-word budget — it will still save.
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
